import PropTypes from "prop-types";
import React, { Component } from "react";
import { batch, connect } from "react-redux";
import {
  fetchData,
  search,
  triggerDownload,
  closeSlider,
} from "../../../../_actions/sidebarSliderActions";
import {
  ADD_MASK,
  REPLACE_BKG_IMAGE,
  REPLACE_META,
  SourceTypes,
  UPDATE_BKG,
} from "../../../../_actions/types";
import { wsAddLayer, wsUpdatePage } from "../../../../_actions/webSocketAction";
import {
  genericImageDownload,
  getImageInfo,
  getUserMetaData,
  onClickSetAsBackground,
} from "../../../../_utils/common";
import {
  ImageItem,
  ImageProvierErrorsInfo,
} from "../../../common/statelessView";
import SuperList from "./SuperList";
import isEqual from "lodash/isEqual";
import { showToast } from "../../../../_utils/showToast";
import { withTranslation } from "react-i18next";

class ImagesList extends Component {
  abortController = new AbortController();
  timers = [];

  render() {
    const { sidebarSlider, story, t } = this.props;
    const { imageSource, loaded, payload, hasMore, imageProviderErrors } =
      sidebarSlider;

    const childElements = payload.map((image, index) => {
      const childPayload = this.preparePayload(
        imageSource,
        image,
        story.contentSize
      );

      return (
        <div
          key={image.id}
          className="item"
          onClick={() => this.addLayerCallback(childPayload, index)}
          data-grid-groupkey={image.groupKey}
        >
          <ImageItem
            data={childPayload}
            key={image.id}
            url={childPayload.content.previewURL}
            alt={image.alt_description}
            width={childPayload.payload ? childPayload.payload.width : 200}
            height={childPayload.payload ? childPayload.payload.height : 200}
            source={imageSource}
            showOverlayInfo={true}
            isUsedInSwiper={false}
          />
        </div>
      );
    });

    const errorsView =
      imageProviderErrors !== null ? (
        <ImageProvierErrorsInfo
          imageProviderErrors={imageProviderErrors}
          imageSource={imageSource}
        />
      ) : null;

    return (
      <>
        <SuperList
          list="image-gallery"
          loaded={loaded}
          searchPlaceholder={t("search.search_photos")}
          notFound={t("search.no_photos_found")}
          errorsView={errorsView}
          hasMore={hasMore}
          loadPage={this.loadPage}
          showRecents
          onClickRecent={this.addToArtboard}
        >
          {childElements}
        </SuperList>
      </>
    );
  }

  shouldComponentUpdate(nextProps) {
    return !isEqual(nextProps, this.props);
  }

  componentWillUnmount() {
    this.abortController.abort();
    this.timers.forEach((timer) => {
      clearInterval(timer);
    });
  }

  loadPage = (groupKey) => {
    // There is no searchText then fetch general images and add into InfiniteGrid
    if (!this.props.searchText) {
      this.props.fetchData(this.props.sidebarSlider.imageSource, {
        ...this.props,
        groupKey,
      });
      return;
    }
    this.props.search(
      this.props.sidebarSlider.imageSource,
      this.props.searchText,
      { ...this.props, groupKey }
    );
  };

  preparePayload = (imageSource, data, contentSize) => {
    let imageInfo = getImageInfo(imageSource, data);
    const message = {
      mime: "image",
      payload: {
        name: "New Image",
        src: imageInfo.url,
        type: "image",
        height: imageInfo.height,
        width: imageInfo.width,
        cover: imageInfo.thumbURL,
        userProperty: {
          source_id: imageInfo.sourceData.source_id,
        },
      },
      content: {
        source: imageInfo.sourceData.source,
        source_url: imageInfo.sourceData.source_url,
        source_id: imageInfo.sourceData.source_id,
        url: imageInfo.url,
        meta: getUserMetaData(imageSource, data),
        previewURL: imageInfo.thumbURL,
      },
    };
    return message;
  };

  updatePage = (data) => {
    const pageId = this.props.editor.activePage.id;
    var message = {
      page: pageId,
      payload: {
        wallpaper: {
          mime: "image",
          url: data.content.url,
        },
      },
    };
    this.props.wsUpdatePage(message);
  };

  addToArtboard = (data, itemIdx) => {
    const { action } = this.props.sidebarSlider;
    const { canvasRef } = this.props;
    if (action === REPLACE_META) {
      const userPropery = {
        source_id: data?.content?.source_id,
      };
      canvasRef.handler.imageHandler.replaceImageSource(
        data.payload.src,
        userPropery,
        { ...data?.content }
      );
    } else if (action === UPDATE_BKG) {
      this.updatePage(data);
    } else if (action === REPLACE_BKG_IMAGE) {
      onClickSetAsBackground(
        this.props.canvasRef,
        data.payload.src,
        data.content
      );
    } else if (action === ADD_MASK) {
      const activeTextObj = canvasRef.handler.canvas.getActiveObject();
      canvasRef.handler.maskHandler.replaceMaskImage(
        activeTextObj,
        data,
        { offsetX: 0, offsetY: 0 },
        "push"
      );
    } else {
      batch(() => {
        this.props.wsAddLayer(this.props.editor.activePage.id, data);
        //TODO move this to backend
        this.props.triggerDownload(
          data.content.meta,
          this.abortController.signal
        );
      });
    }
  };

  addLayerCallback = async (data, index) => {
    const { t } = this.props;
    if (data?.content?.source === SourceTypes.GOOGLE_DRIVE) {
      try {
        const updatedData = await genericImageDownload(
          data,
          null,
          (timer) => {
            this.timers.push(timer);
          },
          this.props
        );
        this.addToArtboard(updatedData, index);
      } catch (e) {
        showToast({
          description: t("errors.image_download_went_Wrong", { ns: "common" }),
          title: t("errors.went_wrong_title", { ns: "common" }),
          status: "error",
        });
        return;
      }
    } else {
      this.addToArtboard(data, index);
    }
    if (this.props.isMobileView) {
      this.props.closeSlider();
    }
  };

  addShapeForImage = (data) => {
    this.props.wsAddLayer(this.props.editor.activePage.id, data);
  };
}

ImagesList.propTypes = {
  editor: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  sidebarSlider: PropTypes.object.isRequired,
  panelType: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  searchText: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  sidebarSlider: state.sidebarSlider,
  errors: state.errors,
  editor: state.editor,
  panelType: state.sidebarSlider.sliderPanelType,
  page: state.sidebarSlider.page,
  searchText: state.sidebarSlider.searchText,
  story: state.story,
  isMobileView: state.app.isMobileView,
});

const mapDispatchToProps = (dispatch) => ({
  wsUpdatePage: (payload) => dispatch(wsUpdatePage(payload)),
  wsAddLayer: (activePageId, payload) =>
    dispatch(wsAddLayer(activePageId, payload)),
  fetchData: (imageSource, props) => dispatch(fetchData(imageSource, props)),
  search: (imageSource, searchTerm, props) =>
    dispatch(search(imageSource, searchTerm, props)),
  triggerDownload: (meta, token) => dispatch(triggerDownload(meta, token)),
  closeSlider: () => dispatch(closeSlider()),
});

export default withTranslation(["common"])(
  connect(mapStateToProps, mapDispatchToProps)(ImagesList)
);
